import { createBrowserRouter, LoaderFunctionArgs, Outlet, redirect } from 'react-router-dom';
import { UnknownRoutePage } from './404';
import { App } from './app';
import { Loader } from './components/loader';
import { ErrorPage } from './error-page';
import { api, LoginResponseDto } from './lib/api';
import { branchLoader } from './loader/branch';
import { companiesLoader, companyLoader } from './loader/company';
import { formLoader } from './loader/form';
import { intakeLoader, patientLoader } from './loader/patient';
import { adminUserLoader, adminUsersLoader, userLoader, usersLoader } from './loader/user';
import { BranchLayout } from './musa/branch/branch-layout';
import { BranchPage } from './musa/branch/branch-page';
import { CompanyLayout } from './musa/company/company-layout';
import { CompanyPage } from './musa/company/company-page';
import { OrganizationLayout } from './musa/organization/organization-layout';
import { OrganizationOverviewPage } from './musa/organization/organization-overview-page';
import { OrganizationUserManagementPage } from './musa/organization/organization-user-management-page';
import { PharmaciesLayout } from './musa/settings/pharmacies/pharmacies-layout';
import { PharmaciesPage } from './musa/settings/pharmacies/pharmacies-page';
import { PharmacyForm } from './musa/settings/pharmacies/pharmacy-form';
import { PhysicianForm } from './musa/settings/physicians/physician-form';
import { PhysiciansLayout } from './musa/settings/physicians/physicians-layout';
import { PhysiciansPage } from './musa/settings/physicians/physicians-page';
import { Settings } from './musa/settings/settings';
import { VisitTypeForm } from './musa/settings/visit-types/visit-type-form';
import { VisitTypesLayout } from './musa/settings/visit-types/visit-types-layout';
import { VisitTypesPage } from './musa/settings/visit-types/visit-types-page';
import { UserLayout } from './musa/user/user-layout';
import { UserPage } from './musa/user/user-page';
import { queryClient } from './providers/query-client';
import { AuthLayout } from './shared/auth/layout';
import { UnauthorizedPage } from './unauthorized';
import { checkPermission, checkPermissionAny } from './utils/check-permissions';
import { PatientsLayout } from './musa/patients/patients-layout';
import { PatientsPage } from './musa/patients/patients-page';
import { claimLoader, claimsInboxLoader, claimsLoader, claimTransactionLoader, patientClaimsLoader } from './loader/claim';
import { cmsLoader } from './loader/cms';
import { TemplatesLayout } from './musa/settings/templates/templates-layout';
import { TemplatesPage } from './musa/settings/templates/templates-page';
import { templateLoader } from './loader/template';
import { FeatureFlagGuard } from './components/feature-flag-guard';
import { AccessGuard, AccessGuardAny } from './components/access-guard';
import { reportLoader } from './loader/report';

async function getOrLoadSession() {
    const cachedSession = queryClient.getQueryData<LoginResponseDto>(['session']);
    if (cachedSession) {
        return cachedSession;
    }
    const session = await queryClient.fetchQuery({
        queryKey: ['session'],
        queryFn: async () => {
            try {
                const { data } = await api.authentication.authenticated();
                return data;
            } catch {
                return null;
            }
        },
    });
    return session;
}

/**
 * Redirects unauthenticated users to login page
 */
async function sessionLoader({ request }: LoaderFunctionArgs) {
    const session = await getOrLoadSession();
    const url = new URL(request.url);
    if (!session?.activeOrg) {
        const params = new URLSearchParams();
        params.set('from', url.pathname);
        return url.pathname === '/' ? redirect('/login') : redirect(`/login?${params.toString()}`);
    }

    if (session.activeOrg?.id === 'admin' && !url.pathname.startsWith('/admin')) {
        return redirect('/admin');
    } else if (session.activeOrg?.id !== 'admin' && url.pathname.startsWith('/admin')) {
        return redirect('/');
    }

    return session;
}

/**
 * Redirects authenticated users when trying to load login routes
 */
async function authenticatedRedirectLoader() {
    const session = await getOrLoadSession();
    if (session?.activeOrg) {
        return redirect('/');
    }
    return null;
}

/**
 * Redirects to unauthorized page based on permissions
 */
function permissionRedirectLoader(page?: string) {
    return async () => {
        const session = await getOrLoadSession();
        if (session?.activeOrg?.id === 'admin') {
            return false;
        }
        if (session && !checkPermission(session, page)) {
            return redirect('/unauthorized');
        }
        return true;
    };
}

function dashboardPermissionLoader() {
    return async () => {
        const session = await getOrLoadSession();
        if (session?.activeOrg?.id === 'admin') {
            return false;
        }

        // Allow access if user has one of: admin-dashboard, qa-dashboard, clinical-dashboard
        if (session && !checkPermissionAny(session, ['admin-dashboard', 'qa-dashboard', 'clinical-dashboard'])) {
            return redirect('/unauthorized');
        }
        return true;
    };
}

export const router = createBrowserRouter(
    [
        {
            id: 'root',
            element: <App />,
            HydrateFallback: Loader,
            loader: sessionLoader,
            ErrorBoundary: ErrorPage,
            children: [
                {
                    id: 'musa',
                    path: '/',
                    children: [
                        {
                            path: '404',
                            index: true,
                            element: <UnknownRoutePage />,
                        },
                        {
                            path: 'unauthorized',
                            index: true,
                            element: <UnauthorizedPage />,
                        },
                        {
                            id: 'boundary',
                            ErrorBoundary: ErrorPage,
                            children: [
                                {
                                    lazy: async () => {
                                        return {
                                            element: (
                                                <FeatureFlagGuard featureFlag="dashboard">
                                                    {{
                                                        success: (
                                                            <AccessGuardAny
                                                                permissions={[
                                                                    'admin-dashboard:read',
                                                                    'clinical-dashboard:read',
                                                                    'qa-dashboard:read',
                                                                ]}>
                                                                {{
                                                                    success: <Outlet />,
                                                                    fallback: (
                                                                        <AccessGuard permission="patient:read">
                                                                            {{
                                                                                success: <PatientsLayout />,
                                                                                fallback: <UnauthorizedPage />,
                                                                            }}
                                                                        </AccessGuard>
                                                                    ),
                                                                }}
                                                            </AccessGuardAny>
                                                        ),
                                                        fallback: (
                                                            <AccessGuard permission="patient:read">
                                                                {{
                                                                    success: <PatientsLayout />,
                                                                    fallback: <UnauthorizedPage />,
                                                                }}
                                                            </AccessGuard>
                                                        ),
                                                    }}
                                                </FeatureFlagGuard>
                                            ),
                                        };
                                    },
                                    children: [
                                        {
                                            index: true,
                                            lazy: async () => {
                                                const { DashboardPage } = await import('./musa/dashboard/dashboard-page');
                                                return {
                                                    element: (
                                                        <FeatureFlagGuard featureFlag="dashboard">
                                                            {{
                                                                success: (
                                                                    <AccessGuardAny
                                                                        permissions={[
                                                                            'admin-dashboard:read',
                                                                            'clinical-dashboard:read',
                                                                            'qa-dashboard:read',
                                                                        ]}>
                                                                        {{
                                                                            success: <DashboardPage />,
                                                                            fallback: (
                                                                                <AccessGuard permission="patient:read">
                                                                                    {{
                                                                                        success: <PatientsPage />,
                                                                                        fallback: <UnauthorizedPage />,
                                                                                    }}
                                                                                </AccessGuard>
                                                                            ),
                                                                        }}
                                                                    </AccessGuardAny>
                                                                ),
                                                                fallback: (
                                                                    <AccessGuard permission="patient:read">
                                                                        {{
                                                                            success: <PatientsPage />,
                                                                            fallback: <UnauthorizedPage />,
                                                                        }}
                                                                    </AccessGuard>
                                                                ),
                                                            }}
                                                        </FeatureFlagGuard>
                                                    ),
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    path: '/schedule',
                                    lazy: async () => {
                                        const { SchedulePage } = await import('./musa/schedule/schedule-page');
                                        return {
                                            element: <SchedulePage />,
                                            loader: async () => {
                                                const permissionChecker = permissionRedirectLoader('visit');
                                                const result = await permissionChecker();
                                                if (result !== true) {
                                                    return null;
                                                }

                                                queryClient.removeQueries({ queryKey: ['schedule'] });
                                                return true;
                                            },
                                        };
                                    },
                                },
                                {
                                    path: '/dashboard',
                                    loader: dashboardPermissionLoader(),
                                    lazy: async () => {
                                        const { DashboardPage } = await import('./musa/dashboard/dashboard-page');
                                        return {
                                            element: <DashboardPage />,
                                        };
                                    },
                                },
                                {
                                    path: '/dashboard/branch/:branchId/visit/:visitId',
                                    loader: dashboardPermissionLoader(),
                                    lazy: async () => {
                                        const { VisitDetailPage } = await import('./musa/dashboard/components/clinical/visit-detail-page');
                                        return {
                                            element: (
                                                <FeatureFlagGuard featureFlag="clinical-dashboard">
                                                    {{
                                                        success: <VisitDetailPage />,
                                                        fallback: <UnauthorizedPage />,
                                                    }}
                                                </FeatureFlagGuard>
                                            ),
                                        };
                                    },
                                },
                                {
                                    loader: permissionRedirectLoader('qa'),
                                    lazy: async () => {
                                        const { QaLayout } = await import('./musa/qa/index');
                                        return {
                                            element: <QaLayout />,
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'qa',
                                            lazy: async () => {
                                                const { QaPage } = await import('./musa/qa/index');
                                                return {
                                                    element: <QaPage />,
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    loader: permissionRedirectLoader('orders'),
                                    lazy: async () => {
                                        const { OrdersLayout } = await import('./musa/orders/index');
                                        return {
                                            element: <OrdersLayout />,
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'orders',
                                            lazy: async () => {
                                                const { OrdersPage } = await import('./musa/orders/index');
                                                return {
                                                    element: <OrdersPage />,
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    loader: permissionRedirectLoader('cms'),
                                    lazy: async () => {
                                        const { CMSLayout } = await import('./musa/cms/index');
                                        return {
                                            element: <CMSLayout />,
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'cms',
                                            lazy: async () => {
                                                const { CMSPage } = await import('./musa/cms/index');
                                                return {
                                                    element: <CMSPage />,
                                                    loader: async () => {
                                                        const permissionChecker = permissionRedirectLoader('cms');
                                                        const result = await permissionChecker();
                                                        if (result !== true) {
                                                            return null;
                                                        }
                                                        return cmsLoader();
                                                    },
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    id: 'payroll',
                                    lazy: async () => {
                                        const { PayrollLayout } = await import('./musa/payroll');
                                        return {
                                            element: <PayrollLayout />,
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'payroll',
                                            lazy: async () => {
                                                const { PayrollView } = await import('./musa/payroll');
                                                return {
                                                    element: <PayrollView />,
                                                    loader: async () => {
                                                        const permissionChecker = permissionRedirectLoader('payroll');
                                                        const result = await permissionChecker();
                                                        return result;
                                                    },
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    id: 'billing',
                                    lazy: async () => {
                                        const { BillingLayout } = await import('./musa/billing/index');
                                        return {
                                            element: <BillingLayout />,
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'billing',
                                            lazy: async () => {
                                                const { BillingPage } = await import('./musa/billing/index');
                                                return {
                                                    element: <BillingPage />,
                                                    loader: async () => {
                                                        const permissionChecker = permissionRedirectLoader('billing');
                                                        const result = await permissionChecker();
                                                        if (result !== true) {
                                                            return null;
                                                        }
                                                        return claimsLoader();
                                                    },
                                                };
                                            },
                                        },
                                        {
                                            path: 'billing/inbox',
                                            lazy: async () => {
                                                const { BillingInbox } = await import('./musa/billing/index');
                                                return {
                                                    element: <BillingInbox />,
                                                    loader: async () => {
                                                        const permissionChecker = permissionRedirectLoader('billing');
                                                        const result = await permissionChecker();
                                                        if (result !== true) {
                                                            return null;
                                                        }
                                                        return claimsInboxLoader();
                                                    },
                                                };
                                            },
                                        },
                                        {
                                            path: 'billing/sent',
                                            lazy: async () => {
                                                const { BillingSent } = await import('./musa/billing/index');
                                                return {
                                                    element: <BillingSent />,
                                                    loader: async () => {
                                                        const permissionChecker = permissionRedirectLoader('billing');
                                                        const result = await permissionChecker();
                                                        if (result !== true) {
                                                            return null;
                                                        }
                                                        return claimsLoader();
                                                    },
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    id: 'billing-inbox',
                                    lazy: async () => {
                                        const { BillingInboxLayout } = await import('./musa/billing/index');
                                        return {
                                            loader: async (args) => {
                                                const permissionChecker = permissionRedirectLoader('billing');
                                                const result = await permissionChecker();
                                                if (result !== true) {
                                                    return null;
                                                }
                                                return claimTransactionLoader(args);
                                            },
                                            element: <BillingInboxLayout />,
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'branch/:branchId/admission/:admissionId/billing/inbox/transaction/:transactionId/year/:year',
                                            lazy: async () => {
                                                const { BillingInboxDetails } = await import('./musa/billing/index');
                                                return {
                                                    element: <BillingInboxDetails />,
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    id: 'billing-sent',
                                    lazy: async () => {
                                        const { BillingSentLayout } = await import('./musa/billing/index');
                                        return {
                                            element: <BillingSentLayout />,
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'branch/:branchId/admission/:admissionId/billing/sent/:claimId',
                                            lazy: async () => {
                                                const { BillingSentDetails } = await import('./musa/billing/index');
                                                return {
                                                    element: <BillingSentDetails />,
                                                    loader: async () => {
                                                        const permissionChecker = permissionRedirectLoader('billing');
                                                        const result = await permissionChecker();
                                                        if (result !== true) {
                                                            return null;
                                                        }
                                                        return claimsLoader();
                                                    },
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    id: 'claim',
                                    lazy: async () => {
                                        const { ClaimLayout } = await import('./musa/billing/claims/claim-layout');
                                        return {
                                            element: <ClaimLayout />,
                                            loader: async (args) => {
                                                const permissionChecker = permissionRedirectLoader('billing');
                                                const result = await permissionChecker();
                                                if (result !== true) {
                                                    return null;
                                                }
                                                return claimLoader(args);
                                            },
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'branch/:branchId/admission/:admissionId/claim/:id',
                                            lazy: async () => {
                                                const { ClaimPage } = await import('./musa/billing/claims/claim-page');
                                                return {
                                                    element: <ClaimPage />,
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    path: '/organization',
                                    loader: permissionRedirectLoader(),
                                    element: <OrganizationLayout />,
                                    children: [
                                        {
                                            index: true,
                                            loader: companiesLoader,
                                            element: <OrganizationOverviewPage />,
                                        },
                                        {
                                            path: 'overview',
                                            loader: companiesLoader,
                                            element: <OrganizationOverviewPage />,
                                        },
                                        {
                                            path: 'user-management',
                                            loader: usersLoader,
                                            element: <OrganizationUserManagementPage />,
                                        },
                                    ],
                                },
                                {
                                    id: 'report',
                                    lazy: async () => {
                                        const { ReportLayout } = await import('./musa/report/index');
                                        // TODO: update when migration for report rules has been set
                                        return {
                                            element: (
                                                <FeatureFlagGuard featureFlag="report-feature-flag">
                                                    {{
                                                        success: (
                                                            <AccessGuard permission="report:read">
                                                                {{
                                                                    success: <ReportLayout />,
                                                                    fallback: <UnauthorizedPage />,
                                                                }}
                                                            </AccessGuard>
                                                        ),
                                                        fallback: <UnauthorizedPage />,
                                                    }}
                                                </FeatureFlagGuard>
                                            ),
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'report',
                                            lazy: async () => {
                                                const { ReportPage } = await import('./musa/report/index');
                                                return {
                                                    element: <ReportPage />,
                                                };
                                            },
                                        },
                                        {
                                            path: 'report/review',
                                            lazy: async () => {
                                                const { ReportReviewList } = await import('./musa/report/index');
                                                return {
                                                    element: <ReportReviewList />,
                                                };
                                            },
                                        },
                                        {
                                            path: 'report/approved',
                                            lazy: async () => {
                                                const { ReportApprovedList } = await import('./musa/report/index');
                                                return {
                                                    element: <ReportApprovedList />,
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    loader: permissionRedirectLoader('report'),
                                    path: '/report/generate',
                                    lazy: async () => {
                                        const { ReportGenerate } = await import('./musa/report/index');
                                        return {
                                            element: (
                                                <FeatureFlagGuard featureFlag="report-feature-flag">
                                                    {{
                                                        success: (
                                                            <AccessGuard permission="report:*">
                                                                {{
                                                                    success: <ReportGenerate />,
                                                                    fallback: <UnauthorizedPage />,
                                                                }}
                                                            </AccessGuard>
                                                        ),
                                                        fallback: <UnauthorizedPage />,
                                                    }}
                                                </FeatureFlagGuard>
                                            ),
                                        };
                                    },
                                },
                                {
                                    path: '/report/review/:id',
                                    lazy: async () => {
                                        const { ReportReview } = await import('./musa/report/index');
                                        return {
                                            element: (
                                                <FeatureFlagGuard featureFlag="report-feature-flag">
                                                    {{
                                                        success: (
                                                            <AccessGuard permission="report:read">
                                                                {{
                                                                    success: <ReportReview />,
                                                                    fallback: <UnauthorizedPage />,
                                                                }}
                                                            </AccessGuard>
                                                        ),
                                                        fallback: <UnauthorizedPage />,
                                                    }}
                                                </FeatureFlagGuard>
                                            ),
                                            loader: async (args: LoaderFunctionArgs) => {
                                                const result = await permissionRedirectLoader('report')();
                                                if (result === true) {
                                                    return reportLoader(args);
                                                }
                                            },
                                        };
                                    },
                                },
                                {
                                    path: '/report/approved/:id',
                                    lazy: async () => {
                                        const { ReportApproved } = await import('./musa/report/index');
                                        return {
                                            element: (
                                                <FeatureFlagGuard featureFlag="report-feature-flag">
                                                    {{
                                                        success: (
                                                            <AccessGuard permission="report:read">
                                                                {{
                                                                    success: <ReportApproved />,
                                                                    fallback: <UnauthorizedPage />,
                                                                }}
                                                            </AccessGuard>
                                                        ),
                                                        fallback: <UnauthorizedPage />,
                                                    }}
                                                </FeatureFlagGuard>
                                            ),
                                            loader: async (args: LoaderFunctionArgs) => {
                                                const result = await permissionRedirectLoader('report')();
                                                if (result === true) {
                                                    return reportLoader(args);
                                                }
                                            },
                                        };
                                    },
                                },
                                {
                                    loader: permissionRedirectLoader(),
                                    path: '/settings',
                                    element: <Settings />,
                                },
                                {
                                    loader: permissionRedirectLoader(),
                                    element: <PhysiciansLayout />,
                                    children: [
                                        {
                                            path: '/settings/physicians',
                                            element: <PhysiciansPage />,
                                        },
                                        {
                                            path: '/settings/physicians/:physicianId/edit',
                                            element: <PhysicianForm />,
                                        },
                                    ],
                                },
                                {
                                    loader: permissionRedirectLoader(),
                                    element: <PharmaciesLayout />,
                                    children: [
                                        {
                                            path: '/settings/pharmacies',
                                            element: <PharmaciesPage />,
                                        },
                                        {
                                            path: '/settings/pharmacy/create',
                                            element: <PharmacyForm />,
                                        },
                                        {
                                            path: '/settings/pharmacies/:pharmacyId/edit',
                                            element: <PharmacyForm />,
                                        },
                                    ],
                                },
                                {
                                    element: <VisitTypesLayout />,
                                    children: [
                                        {
                                            path: '/settings/visit-types',
                                            element: <VisitTypesPage />,
                                        },
                                        {
                                            path: '/settings/visit-type/create',
                                            element: <VisitTypeForm />,
                                        },
                                        {
                                            path: '/settings/visit-type/:visitTypeId/edit',
                                            element: <VisitTypeForm />,
                                        },
                                    ],
                                },
                                {
                                    element: <TemplatesLayout />,
                                    children: [
                                        {
                                            path: '/settings/templates',
                                            element: <TemplatesPage />,
                                            loader: templateLoader,
                                        },
                                    ],
                                },
                                {
                                    path: '/settings/payroll-schedules',
                                    loader: permissionRedirectLoader('payroll'),
                                    lazy: async () => {
                                        const { PayrollScheduleManagementPage } = await import(
                                            './musa/payroll/payroll-schedule-management-page'
                                        );
                                        return {
                                            element: <PayrollScheduleManagementPage />,
                                        };
                                    },
                                },
                                {
                                    path: '/settings/insurance-config',
                                    loader: permissionRedirectLoader('insurance'),
                                    lazy: async () => {
                                        const { InsuranceConfigPage } = await import('./musa/settings/insurance/insurance-config-page.tsx');
                                        return {
                                            element: <InsuranceConfigPage />,
                                        };
                                    },
                                },
                                {
                                    path: '/settings/report-config',
                                    loader: permissionRedirectLoader('report'),
                                    lazy: async () => {
                                        const { ReportConfigPage } = await import('./musa/settings/report/report-config-page');

                                        return {
                                            element: (
                                                <FeatureFlagGuard featureFlag="report-feature-flag">
                                                    {{
                                                        success: (
                                                            <AccessGuard permission="*:*">
                                                                {{
                                                                    success: <ReportConfigPage />,
                                                                    fallback: <UnauthorizedPage />,
                                                                }}
                                                            </AccessGuard>
                                                        ),
                                                        fallback: <UnauthorizedPage />,
                                                    }}
                                                </FeatureFlagGuard>
                                            ),
                                        };
                                    },
                                },
                                {
                                    loader: permissionRedirectLoader(),
                                    element: <CompanyLayout />,
                                    children: [
                                        {
                                            id: 'company-details',
                                            path: 'company/:companyId',
                                            loader: companyLoader,
                                            element: <CompanyPage />,
                                        },
                                        {
                                            path: 'company/create',
                                            element: <CompanyPage />,
                                        },
                                    ],
                                },
                                {
                                    loader: permissionRedirectLoader(),
                                    element: <BranchLayout />,
                                    children: [
                                        {
                                            id: 'branch-details',
                                            path: 'company/:companyId/branch/:branchId',
                                            loader: branchLoader,
                                            element: <BranchPage />,
                                        },
                                        {
                                            id: 'create-branch',
                                            path: 'company/:companyId/branch/create',
                                            element: <BranchPage />,
                                        },
                                    ],
                                },
                                {
                                    id: 'user-details',
                                    loader: async (args) => {
                                        const permissionChecker = permissionRedirectLoader();
                                        const result = await permissionChecker();
                                        if (result !== true) {
                                            return null;
                                        }
                                        return userLoader(args);
                                    },
                                    element: <UserLayout />,
                                    children: [
                                        {
                                            path: 'user/create',
                                            element: <UserPage />,
                                        },
                                        {
                                            path: 'user/:userId',
                                            element: <UserPage />,
                                        },
                                    ],
                                },
                                {
                                    loader: permissionRedirectLoader('patient'),
                                    lazy: async () => {
                                        const { PatientsLayout } = await import('./musa/patients/index');
                                        return {
                                            element: <PatientsLayout />,
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'patients',
                                            lazy: async () => {
                                                const { PatientsPage } = await import('./musa/patients/index');
                                                return {
                                                    element: <PatientsPage />,
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    id: 'admission',
                                    lazy: async () => {
                                        const { IntakeLayout } = await import('./musa/intake/index');
                                        return {
                                            element: <IntakeLayout />,
                                            loader: async (args: LoaderFunctionArgs) => {
                                                const result = await permissionRedirectLoader('patient')();
                                                if (result === true) {
                                                    return intakeLoader(args);
                                                }
                                            },
                                        };
                                    },
                                    children: [
                                        {
                                            path: 'intake',
                                            lazy: async () => {
                                                const { IntakePage } = await import('./musa/intake/index');
                                                return {
                                                    element: <IntakePage key="create" />,
                                                };
                                            },
                                        },
                                        {
                                            path: 'branch/:branchId/admission/:admissionId/intake',
                                            lazy: async () => {
                                                const { IntakePage } = await import('./musa/intake/index');
                                                return {
                                                    element: <IntakePage key="edit" />,
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    id: 'patient',
                                    path: 'branch/:branchId/admission/:admissionId',
                                    loader: async (args: LoaderFunctionArgs) => {
                                        const result = await permissionRedirectLoader('patient')();
                                        if (result === true) {
                                            return patientLoader(args);
                                        }
                                    },
                                    shouldRevalidate: ({ currentUrl, currentParams, nextUrl, nextParams, defaultShouldRevalidate }) => {
                                        // We do not need to revalidate when the form section changes
                                        if (currentUrl.pathname === nextUrl.pathname && !!currentParams.formId && !!nextParams.formId) {
                                            // This means if an invalidate happens due to an event in the same "section" it will invalidate
                                            return currentUrl.search === nextUrl.search;
                                        }
                                        return defaultShouldRevalidate;
                                    },
                                    children: [
                                        {
                                            lazy: async () => {
                                                const { PatientLayout } = await import('./musa/patient/index');
                                                return {
                                                    element: <PatientLayout />,
                                                };
                                            },
                                            children: [
                                                {
                                                    path: 'patient',
                                                    lazy: async () => {
                                                        const { PatientOverview } = await import('./musa/patient/index');

                                                        return {
                                                            element: <PatientOverview />,
                                                        };
                                                    },
                                                },
                                                {
                                                    path: 'plan',
                                                    lazy: async () => {
                                                        const { PatientPlanOfCare } = await import('./musa/patient/index');

                                                        return {
                                                            element: <PatientPlanOfCare />,
                                                        };
                                                    },
                                                },
                                                {
                                                    path: 'medications',
                                                    lazy: async () => {
                                                        const { PatientMedications } = await import('./musa/patient/index');

                                                        return {
                                                            element: <PatientMedications />,
                                                        };
                                                    },
                                                },
                                                {
                                                    path: 'wounds',
                                                    lazy: async () => {
                                                        const { PatientWounds } = await import('./musa/patient/index');

                                                        return {
                                                            element: <PatientWounds />,
                                                        };
                                                    },
                                                },
                                                {
                                                    path: 'schedule',
                                                    lazy: async () => {
                                                        const { PatientSchedule } = await import('./musa/patient/index');

                                                        return {
                                                            element: <PatientSchedule />,
                                                        };
                                                    },
                                                },
                                                {
                                                    path: 'profile',
                                                    lazy: async () => {
                                                        const { PatientProfileLayout } = await import('./musa/patient/profile');

                                                        return {
                                                            element: <PatientProfileLayout />,
                                                        };
                                                    },
                                                    children: [
                                                        {
                                                            path: '',
                                                            lazy: async () => {
                                                                const { PatientProfileSummary } = await import('./musa/patient/profile');

                                                                return {
                                                                    element: <PatientProfileSummary />,
                                                                };
                                                            },
                                                        },
                                                        {
                                                            path: 'insurance',
                                                            lazy: async () => {
                                                                const { PatientProfileInsurance } = await import('./musa/patient/profile');

                                                                return {
                                                                    element: <PatientProfileInsurance />,
                                                                };
                                                            },
                                                        },
                                                        {
                                                            path: 'caregivers',
                                                            lazy: async () => {
                                                                const { PatientProfileCaregivers } = await import('./musa/patient/profile');

                                                                return {
                                                                    element: <PatientProfileCaregivers />,
                                                                };
                                                            },
                                                        },
                                                        {
                                                            path: 'providers',
                                                            lazy: async () => {
                                                                const { PatientProfileProviders } = await import('./musa/patient/profile');

                                                                return {
                                                                    element: <PatientProfileProviders />,
                                                                };
                                                            },
                                                        },
                                                    ],
                                                },
                                                {
                                                    path: 'documents',
                                                    lazy: async () => {
                                                        const { PatientDocumentsLayout } = await import('./musa/patient/documents/index');
                                                        return {
                                                            element: <PatientDocumentsLayout />,
                                                        };
                                                    },
                                                    children: [
                                                        {
                                                            path: '',
                                                            lazy: async () => {
                                                                const { PatientForms } = await import('./musa/patient/documents/index');

                                                                return {
                                                                    element: <PatientForms />,
                                                                };
                                                            },
                                                        },
                                                        {
                                                            path: 'files',
                                                            lazy: async () => {
                                                                const { PatientFiles } = await import('./musa/patient/documents/index');

                                                                return {
                                                                    element: <PatientFiles />,
                                                                };
                                                            },
                                                        },
                                                    ],
                                                },
                                                {
                                                    id: 'patient-claims',
                                                    path: 'claims',
                                                    lazy: async () => {
                                                        const { PatientBillingLayout } = await import('./musa/patient/billing/index');
                                                        return {
                                                            element: <PatientBillingLayout />,
                                                            loader: async (args) => {
                                                                const result = await permissionRedirectLoader('insurance')();
                                                                if (result === true) {
                                                                    return patientClaimsLoader(args);
                                                                }
                                                            },
                                                        };
                                                    },
                                                    children: [
                                                        {
                                                            path: '',
                                                            lazy: async () => {
                                                                const { PatientBillingPage } = await import('./musa/patient/billing/index');

                                                                return {
                                                                    element: <PatientBillingPage />,
                                                                };
                                                            },
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                        {
                                            id: 'form',
                                            path: 'forms/:formId',
                                            loader: formLoader,
                                            shouldRevalidate: ({ currentUrl, nextUrl, defaultShouldRevalidate }) => {
                                                // We do not need to revalidate when the form section changes
                                                if (currentUrl.pathname === nextUrl.pathname) {
                                                    return currentUrl.search === nextUrl.search;
                                                }
                                                return defaultShouldRevalidate;
                                            },
                                            lazy: async () => {
                                                const { FormIndex } = await import('./musa/forms/index');
                                                return {
                                                    element: <FormIndex />,
                                                };
                                            },
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            id: 'admin',
                            path: '/admin',
                            ErrorBoundary: ErrorPage,
                            children: [
                                {
                                    index: true,
                                    loader: adminUsersLoader,
                                    lazy: async () => {
                                        const { UsersPage } = await import('./admin/index');
                                        return {
                                            element: <UsersPage />,
                                        };
                                    },
                                },
                                {
                                    path: 'users',
                                    children: [
                                        {
                                            path: '',
                                            loader: adminUsersLoader,
                                            lazy: async () => {
                                                const { UsersPage } = await import('./admin/index');
                                                return {
                                                    element: <UsersPage />,
                                                };
                                            },
                                        },
                                        {
                                            path: 'create',
                                            lazy: async () => {
                                                const { UserPage } = await import('./admin/users/user-page');
                                                return {
                                                    element: <UserPage />,
                                                };
                                            },
                                        },
                                        {
                                            path: ':userId',
                                            loader: adminUserLoader,
                                            lazy: async () => {
                                                const { UserPage } = await import('./admin/users/user-page');
                                                return {
                                                    element: <UserPage />,
                                                };
                                            },
                                        },
                                    ],
                                },
                                {
                                    path: 'organizations',
                                    lazy: async () => {
                                        const { OrganizationsPage } = await import('./admin/index');
                                        return {
                                            element: <OrganizationsPage />,
                                        };
                                    },
                                },
                                {
                                    path: 'organizations/create',
                                    lazy: async () => {
                                        const { CreateOrganizationPage } = await import('./admin/index');
                                        return {
                                            element: <CreateOrganizationPage />,
                                        };
                                    },
                                },
                                {
                                    path: 'organizations/:orgId',
                                    lazy: async () => {
                                        const { OrganizationDetailPage } = await import('./admin/organizations/organization-page');
                                        return {
                                            element: <OrganizationDetailPage />,
                                        };
                                    },
                                },
                                {
                                    path: 'feature-flags',
                                    lazy: async () => {
                                        const { FeatureFlagsPage } = await import('./admin/feature-flags/feature-flags-page');
                                        return {
                                            element: <FeatureFlagsPage />,
                                        };
                                    },
                                },
                                {
                                    path: 'audit-logs',
                                    lazy: async () => {
                                        const { AuditLogsPage } = await import('./admin/audit/audit-logs-page');
                                        return {
                                            element: <AuditLogsPage />,
                                        };
                                    },
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'login-root',
            element: <AuthLayout />,
            errorElement: <ErrorPage />,
            loader: authenticatedRedirectLoader,
            children: [
                {
                    id: 'login',
                    path: '/login',
                    lazy: async () => {
                        const { LoginPage } = await import('./shared/auth/index');
                        return {
                            element: <LoginPage />,
                        };
                    },
                },
                {
                    id: 'reset-password',
                    path: '/reset-password',
                    lazy: async () => {
                        const { ResetPasswordPage } = await import('./shared/auth/index');
                        return {
                            element: <ResetPasswordPage />,
                        };
                    },
                },
                {
                    id: 'set-password-confirm',
                    path: '/set-password/confirm',
                    lazy: async () => {
                        const { SetPasswordConfirmPage } = await import('./shared/auth/index');
                        return {
                            element: <SetPasswordConfirmPage />,
                        };
                    },
                },
                {
                    id: 'reset-password-confirm',
                    path: '/reset-password/confirm',
                    lazy: async () => {
                        const { ResetPasswordConfirmPage } = await import('./shared/auth/index');
                        return {
                            element: <ResetPasswordConfirmPage />,
                        };
                    },
                },
            ],
        },
    ],
    { future: { v7_partialHydration: true } },
);
